/**
 * SAUSAGE.JSX - Composant pour afficher le modèle 3D de la saucisse
 * 
 * Affiche le modèle avec ses matériaux d'origine (exportés depuis Blender)
 * 
 * ANIMATIONS :
 * 
 * 1. RIRE AU CLIC :
 *    - hihi_body : Animation des bones (corps)
 *    - hihi : Animation du shape key (yeux)
 *    - Déclenchées ensemble en one-shot au clic sur le modèle
 * 
 * 2. DANSE QUAND LA RADIO JOUE (SYNCHRONISÉE AU BPM) :
 *    - hihi : Ouverture/fermeture des yeux
 *      * Au DÉMARRAGE de la danse : joue la 1ère moitié (0-50% = ouverture)
 *      * À L'ARRÊT de la danse : joue la 2ème moitié (50-100% = fermeture)
 *    - danceBlink : Clignement des yeux en boucle continue (optionnel)
 *    - danceBaby : Animation unique contrôlée manuellement
 *      * 0-50% de l'animation : mouvement vers la GAUCHE
 *      * 50-100% de l'animation : mouvement vers la DROITE
 *      * Synchronisation parfaite avec le BPM de la musique
 *    - Démarre automatiquement quand la radio (mood1) joue
 * 
 * TECHNIQUE :
 * - Utilise SkeletonUtils.clone() pour préserver les bones/skeleton
 * - Contrôle manuel du temps d'animation via action.time dans useFrame
 * - Synchronisation BPM : 1 cycle = 2 beats (gauche + droite)
 * - Détection de l'état de la radio via useSounds().isPlaying('mood1')
 * - BPM configurés dans src/config/sounds.js (SONG_BPM)
 * 
 * ORIENTATION :
 * - Blender utilise Z-up, Three.js utilise Y-up
 * - Une rotation peut être nécessaire pour l'alignement correct
 */

import { useRef, useEffect, useMemo, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { useAnimations } from '@react-three/drei'
import { useModel } from '../hooks/useResources'
import { useControls, button } from 'leva'
import { SkeletonUtils } from 'three-stdlib'
import { useSounds } from '../hooks/useSounds'
import { LoopOnce, LoopRepeat } from 'three'
import { SONG_BPM } from '../config/sounds'

export default function Sausage({ scale = 1 }) {
  // ============================================
  // REFS & STATE
  // ============================================
  
  // Référence vers le group contenant le modèle (nécessaire pour useAnimations)
  const groupRef = useRef()
  
  // State pour tracker si les animations de danse sont actives
  const [isDancing, setIsDancing] = useState(false)
  
  // Ref pour éviter de relancer les animations à chaque frame
  const wasPlayingRef = useRef(false)
  
  // Ref pour tracker le temps de beat pour la synchronisation BPM
  const beatTimeRef = useRef(0)
  
  // Ref pour tracker le nom de la musique actuellement jouée
  const currentSongRef = useRef('mood1')
  
  // ============================================
  // HOOKS AUDIO
  // ============================================
  
  const { isPlaying, play } = useSounds()
  
  // ============================================
  // CHARGEMENT DU MODÈLE & ANIMATIONS
  // ============================================
  
  // Charger le modèle GLB avec ses animations
  const { scene, animations } = useModel('sausage')
  
  // Hook pour gérer les animations
  // useAnimations retourne { actions, names, mixer }
  const { actions, names, mixer } = useAnimations(animations, groupRef)
  
  // ============================================
  // DEBUG : VÉRIFICATION DES ANIMATIONS
  // ============================================
  // Supprimé pour production
  
  // ============================================
  // CLEANUP AU DÉMONTAGE
  // ============================================
  
  useEffect(() => {
    // Cleanup à la destruction du composant
    return () => {
      stopDanceAnimations()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  // ============================================
  // FONCTIONS DE CONTRÔLE DES ANIMATIONS
  // ============================================
  
  /**
   * Joue les deux animations simultanément (bones + shape key) en ONE-SHOT
   * Animation de rire/guilis au clic
   */
  const playGuilisAnimation = () => {
    // Sauvegarder l'état de danse avant d'arrêter
    const wasDancingBeforeClick = isDancing
    
    // IMPORTANT : Arrêter les animations de danse pour éviter conflit sur les bones
    if (isDancing) {
      // Arrêter danceBaby (pas danceBlink car c'est juste les yeux)
      if (actions['danceBaby']) {
        actions['danceBaby'].stop()
      }
    }
    
    // Jouer le son de rire "hihi"
    play('hihi')
    
    // Animation des bones (corps) - rire
    if (actions['hihi_body']) {
      actions['hihi_body'].reset()
      actions['hihi_body'].setLoop(LoopOnce, 1)
      actions['hihi_body'].clampWhenFinished = false // Ne pas rester sur dernière frame
      actions['hihi_body'].play()
      
      // Après la fin du rire, reprendre la danse si elle était active
      if (wasDancingBeforeClick && mixer) {
        const hihiDuration = actions['hihi'].getClip().duration * 1000 // en ms
        setTimeout(() => {
          // Relancer l'animation de danse
          if (isDancing && actions['danceBaby']) {
            actions['danceBaby'].reset()
            actions['danceBaby'].play()
            actions['danceBaby'].paused = true
            beatTimeRef.current = 0 // Réinitialiser le temps pour repartir en rythme
          }
        }, hihiDuration)
      }
    }
    
    // Animation du shape key (yeux)
    if (actions['hihi']) {
      actions['hihi'].reset()
      actions['hihi'].setLoop(LoopOnce, 1)
      actions['hihi'].clampWhenFinished = false
      actions['hihi'].play()
    }
  }
  
  /**
   * Arrête l'animation Guilis
   */
  const stopGuilisAnimation = () => {
    if (actions['hihi']) {
      actions['hihi'].fadeOut(0.3)
    }
    if (actions['Guilis']) {
      actions['Guilis'].fadeOut(0.3)
    }
  }
  
  /**
   * Démarre les animations de danse synchronisées au BPM
   * - danceBlink : boucle continue (clignement des yeux)
   * - danceBaby : animation unique contrôlée manuellement selon le BPM
   * - hihi : première moitié jouée au démarrage (ouverture yeux)
   */
  const startDanceAnimations = (songName = 'mood1') => {
    // Sauvegarder le nom de la musique
    currentSongRef.current = songName
    
    // 1. Jouer la première moitié de l'animation hihi (ouverture des yeux)
    if (actions['hihi']) {
      const hihiAction = actions['hihi']
      const hihiClip = hihiAction.getClip()
      const halfDuration = hihiClip.duration / 2
      
      hihiAction.reset()
      hihiAction.setLoop(LoopOnce, 1)
      hihiAction.clampWhenFinished = true // Rester sur la dernière frame (moitié)
      hihiAction.time = 0 // Commencer au début
      hihiAction.play()
      
      // Arrêter l'animation à la moitié
      hihiAction.setEffectiveTimeScale(1)
      setTimeout(() => {
        hihiAction.paused = true
      }, halfDuration * 1000)
    }
    
    // 2. Lancer danceBlink en boucle (yeux) - optionnel
    if (actions['danceBlink']) {
      actions['danceBlink'].reset()
      actions['danceBlink'].setLoop(LoopRepeat, Infinity)
      actions['danceBlink'].play()
    }
    
    // 3. Préparer l'animation danceBaby (contrôle manuel dans useFrame)
    if (actions['danceBaby']) {
      actions['danceBaby'].reset()
      actions['danceBaby'].setLoop(LoopRepeat, Infinity)
      actions['danceBaby'].play()
      actions['danceBaby'].paused = true // Mettre en pause pour contrôle manuel
      
      // Réinitialiser le temps de beat
      beatTimeRef.current = 0
    }
  }
  
  /**
   * Arrête toutes les animations de danse
   * - Joue la deuxième moitié de hihi (fermeture des yeux)
   */
  const stopDanceAnimations = () => {
    // 1. Jouer la deuxième moitié de l'animation hihi (fermeture des yeux)
    if (actions['hihi']) {
      const hihiAction = actions['hihi']
      const hihiClip = hihiAction.getClip()
      const halfDuration = hihiClip.duration / 2
      
      hihiAction.reset()
      hihiAction.setLoop(LoopOnce, 1)
      hihiAction.clampWhenFinished = false // Revenir à la position initiale
      hihiAction.time = halfDuration // Commencer à la moitié
      hihiAction.play()
    }
    
    // 2. Arrêter danceBlink
    if (actions['danceBlink']) {
      actions['danceBlink'].fadeOut(0.5)
    }
    
    // 3. Arrêter danceBaby
    if (actions['danceBaby']) {
      actions['danceBaby'].fadeOut(0.5)
    }
    
    // Réinitialiser le temps de beat
    beatTimeRef.current = 0
  }
  
  // ============================================
  // USEFRAME : RÉAGIR À L'ÉTAT DE LA RADIO & CONTRÔLER L'ANIMATION
  // ============================================
  
  useFrame((state, delta) => {
    // Vérifier si la radio joue actuellement
    const isRadioPlaying = isPlaying('mood1')
    
    // Si état a changé (radio vient de s'allumer ou s'éteindre)
    if (isRadioPlaying !== wasPlayingRef.current) {
      // Attendre que les actions soient chargées
      if (Object.keys(actions).length === 0) {
        return
      }
      
      if (isRadioPlaying) {
        startDanceAnimations('mood1')
        setIsDancing(true)
      } else {
        stopDanceAnimations()
        setIsDancing(false)
      }
      
      // Mémoriser le nouvel état
      wasPlayingRef.current = isRadioPlaying
    }
    
    // ============================================
    // CONTRÔLE MANUEL DE L'ANIMATION DANCEBABY (SYNCHRONISATION BPM)
    // ============================================
    
    if (isDancing && isRadioPlaying && actions['danceBaby']) {
      const action = actions['danceBaby']
      const clip = action.getClip()
      
      // Récupérer le BPM de la musique actuelle
      const bpm = SONG_BPM[currentSongRef.current] || 120
      const beatDuration = 60 / bpm // Durée d'un beat en secondes
      
      // Accumuler le temps
      beatTimeRef.current += delta
      
      // Un cycle complet = 2 beats (gauche + droite)
      // Beat 1: animation de 0% à 50% (gauche)
      // Beat 2: animation de 50% à 100% (droite)
      const cycleDuration = beatDuration * 2
      
      // Normaliser le temps (0-1) sur un cycle complet
      const normalizedTime = (beatTimeRef.current % cycleDuration) / cycleDuration
      
      // Mapper le temps normalisé vers le temps de l'animation
      // L'animation danceBaby va de 0 à clip.duration
      // - 0-50% : gauche (0 à clip.duration/2)
      // - 50-100% : droite (clip.duration/2 à clip.duration)
      action.time = normalizedTime * clip.duration
      
      // S'assurer que l'animation est active (pas en pause)
      if (action.paused) {
        action.paused = false
      }
      
      // Mettre à jour le mixer avec delta = 0 (on contrôle manuellement le temps)
      if (mixer) {
        mixer.update(0)
      }
    }
  })
  

  // ============================================
  // CONTRÔLES LEVA - POSITION, ROTATION & ANIMATION
  // ============================================
  
  const { posX, posY, posZ, rotationX, rotationY, rotationZ } = useControls('Sausage', {
    '---1': { value: '--- Position ---', editable: false },
    posX: {
      value: 2.4,
      min: -10,
      max: 10,
      step: 0.1,
      label: 'Position X'
    },
    posY: {
      value: -1.8,
      min: -10,
      max: 10,
      step: 0.1,
      label: 'Position Y'
    },
    posZ: {
      value: 0,
      min: -10,
      max: 10,
      step: 0.1,
      label: 'Position Z'
    },
    '---2': { value: '--- Rotation ---', editable: false },
    rotationX: {
      value: Math.PI / 2, // 90° (conversion Z-up vers Y-up)
      min: -Math.PI,
      max: Math.PI,
      step: 0.01,
      label: 'Rotation X (rad)'
    },
    rotationY: {
      value: 0,
      min: Math.PI,
      max: -Math.PI,
      step: 0.01,
      label: 'Rotation Y (rad)'
    },
    rotationZ: {
      value: 0,
      min: -Math.PI,
      max: Math.PI,
      step: 0.01,
      label: 'Rotation Z (rad)'
    },
    
    // Séparateur visuel
    '---3': { value: '--- Animation ---', editable: false },
    
    // ==================
    // BOUTONS ANIMATION
    // ==================
    
    // Bouton pour lancer l'animation Guilis (rire - bones + shape key ensemble)
    playGuilis: button(() => {
      playGuilisAnimation()
    }, { label: '😂 Rire (hihi+Guilis)' }),
    
    // Bouton pour lancer les animations de danse
    playDance: button(() => {
      startDanceAnimations()
    }, { label: '💃 Danse (Start)' }),
    
    // Bouton pour arrêter les animations de danse
    stopDance: button(() => {
      stopDanceAnimations()
    }, { label: '⏹️ Danse (Stop)' }),
    
    // Séparateur visuel
    '---4': { value: '--- Actions ---', editable: false },
    
    // ==================
    // BOUTONS D'ACTION
    // ==================
    
    // Bouton "Log Values" : récupère les valeurs depuis le DOM (inputs HTML de Leva)
    // Méthode simple et robuste : Leva génère des inputs avec des IDs prévisibles
    logValues: button(() => {
      // Logs désactivés pour production
      // Si besoin, décommenter les lignes suivantes :
      /*
      const getPosX = parseFloat(document.getElementById('Sausage.posX')?.value || posX)
      const getPosY = parseFloat(document.getElementById('Sausage.posY')?.value || posY)
      const getPosZ = parseFloat(document.getElementById('Sausage.posZ')?.value || posZ)
      const getRotX = parseFloat(document.getElementById('Sausage.rotationX')?.value || rotationX)
      const getRotY = parseFloat(document.getElementById('Sausage.rotationY')?.value || rotationY)
      const getRotZ = parseFloat(document.getElementById('Sausage.rotationZ')?.value || rotationZ)
      console.log({ getPosX, getPosY, getPosZ, getRotX, getRotY, getRotZ })
      */
    }, { label: '📋 Log Values' }),
    
    // Bouton "Reset" : réinitialise toutes les valeurs aux defaults
    reset: button((get, set) => {
      set('Sausage', {
        posX: 3,
        posY: 0,
        posZ: 0,
        rotationX: Math.PI / 2,
        rotationY: 0,
        rotationZ: 0
      })
    }, { label: '🔄 Reset' })
    
  }, { collapsed: true })

  // ============================================
  // PRÉPARATION DU MODÈLE
  // ============================================
  
  // Clone le modèle avec SkeletonUtils pour préserver les bones/skeleton
  // scene.clone() standard ne fonctionne PAS avec les animations de bones
  // SkeletonUtils.clone() copie correctement le skeleton et ses relations
  const sausageClone = useMemo(() => {
    const clone = SkeletonUtils.clone(scene)
    
    // Parcourt tous les meshes du modèle et active la projection d'ombres
    // Inclut les SkinnedMesh (meshes avec bones) en plus des Mesh classiques
    clone.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        child.castShadow = true      // Le mesh projette des ombres
        child.receiveShadow = true   // Le mesh reçoit des ombres
      }
    })
    
    return clone
  }, [scene])

  // ============================================
  // GESTIONNAIRE DE CLIC
  // ============================================
  
  const handleClick = (e) => {
    e.stopPropagation() // Empêcher la propagation de l'événement
    playGuilisAnimation()
  }
  
  // ============================================
  // RENDU
  // ============================================
  
  return (
    // Wrapper dans un <group> pour que useAnimations puisse attacher le AnimationMixer
    // La ref doit pointer vers le conteneur parent, pas directement vers le primitive
    <group ref={groupRef}>
      <primitive 
        object={sausageClone}  // Clone du modèle avec ombres configurées
        position={[posX, posY, posZ]}
        scale={scale}
        rotation={[rotationX, rotationY, rotationZ]}
        onClick={handleClick}  // Gestionnaire de clic pour jouer l'animation
        onPointerOver={(e) => {
          e.stopPropagation()
          document.body.style.cursor = 'pointer' // Changer le curseur au survol
        }}
        onPointerOut={(e) => {
          e.stopPropagation()
          document.body.style.cursor = 'default' // Restaurer le curseur
        }}
      />
    </group>
  )
}



